<template>
    <div
        class="tw-my-4"
    >
        <Heading
            :title="travelMessageInputTitle"
            :size="isAsiInquiry ? 2 : 3"
            :class="{ '!tw-text-[32px] !tw-leading-[1.1] !tw-mt-20': isAsiInquiry }"
        />
        <country-questions :country="country" :country-name="countryName" v-if="isFollowUpInquiry" />
        <textarea
            class="tw-w-full tw-p-2 tw-font-thin tw-resize-none tw-h-52 tw-overflow-x-hidden tw-rounded-sm"
            :class="[
                isActive ? 'tw-border-2 tw-border-black' : isHover ? 'tw-border tw-border-black' : 'tw-border tw-border-gray-300'
            ]"
            :placeholder="travelMessageInputPlaceholder"
            @blur="handleBlur"
            @focus="isActive = true"
            @mouseover="isHover = true"
            @mouseleave="isHover = false"
            v-model="message"
        />
        <div 
            v-if="$route.name.includes('updateMessage')"
            class="tw-text-xs tw-pointer-events-none tw-text-right"
            :style="{ color: progressBar['background-color'] }"
        >
            {{ message.length }} Zeichen
        </div>
        <div
            v-if="!isFollowUpInquiry || $route.name.includes('updateMessage')"
            class="tw-w-full tw-h-1 tw-bg-gray-300 tw-rounded-lg tw-mb-2"
        >
            <div
                class="tw-h-full tw-rounded-lg"
                :style="progressBar"
            />
        </div>
        <InfoSnippet
            :icon="motivator.icon"
            hasBackground
            hasNoYSpacing
            class="tw-my-2"
            ref="motivator"
        >
            {{ motivator.text }}
        </InfoSnippet>
    </div>
</template>

<script>
import { LONG_MESSAGE_THRESHOLD } from '../mixin/acquisitionConstants.js';
import CountryQuestions from "./CountryQuestions.vue";
import InfoSnippet from "./InfoSnippet.vue";

export default {
    name: "TravelMessageInput",
    components: {
        CountryQuestions,
        InfoSnippet,
    },
    props: {
        countryName: {
            type: String,
            required: true
        },
        country: {
            type: Object,
            required: false
        },
        inquiryType: {
            type: String,
            required: false
        },
        value: {
            type: String,
            default: ''
        }
    },
    data: () => ({
        message: '',
        isActive: false,
        isHover: false,
        motivatorSteps: [
            {
                icon: 'PenIcon',
                text: 'Erzähle uns von Deiner Traumreise. Je mehr Details, desto persönlicher wird Dein Angebot.',
                from: 0,
                to: 99,
                barColor: '#CCD5E0',
            },
            {
                icon: 'RocketIcon',
                text: 'Klasse! Je lebhafter wir Deine Reise vor Augen haben, desto besser wird das Angebot unserer Partner.',
                from: 100,
                to: 224,
                barColor: '#FFAD00',
            },
            {
                icon: 'TrophyIcon',
                text: 'Ausgezeichnet, vielen Dank! Das hilft unseren Experten enorm, ein maßgeschneidertes Angebot zu erstellen.',
                from: LONG_MESSAGE_THRESHOLD,
                to: 99999999,
                barColor: '#02B504',
            },
        ],
        // ASI variant — same shape, lowercase du/dein/deine mid-sentence
        // to match the asi.at house style.
        asiMotivatorSteps: [
            {
                icon: 'PenIcon',
                text: 'Erzähle uns von deiner Traumreise. Je mehr Details, desto persönlicher wird dein Angebot.',
                from: 0,
                to: 99,
                barColor: '#CCD5E0',
            },
            {
                icon: 'RocketIcon',
                text: 'Klasse! Je lebhafter wir deine Reise vor Augen haben, desto besser wird das Angebot unserer Partner.',
                from: 100,
                to: 224,
                barColor: '#FFAD00',
            },
            {
                icon: 'TrophyIcon',
                text: 'Ausgezeichnet, vielen Dank! Das hilft unseren Experten enorm, ein maßgeschneidertes Angebot zu erstellen.',
                from: LONG_MESSAGE_THRESHOLD,
                to: 99999999,
                barColor: '#02B504',
            },
        ],
        followUpMotivatorSteps: [
            {
                text: 'Erzähle uns von Deinen Wünschen. Je mehr wir wissen, desto besser wird Deine Reise.',
                from: 0,
                to: 99,
                barColor: '#CCD5E0',
            },
            {
                text: 'Weiter so! Unsere lokalen Experten können die Reise immer besser für Dich planen',
                from: 100,
                to: 224,
                barColor: '#FFAD00',
            },
            {
                text: 'Perfekt, Deine Reise nimmt Form an! Erzähle uns gerne mehr.',
                from: LONG_MESSAGE_THRESHOLD,
                to: 99999999,
                barColor: '#02B504',
            },
        ],
        updateMessageMotivatorSteps: [
            {
                text: 'Der Text sollte idealerweise 250 Zeichen oder mehr umfassen.',
                from: 0,
                to: 99,
                barColor: '#CCD5E0',
            },
            {
                text: 'Der Text sollte idealerweise 250 Zeichen oder mehr umfassen.',
                from: 100,
                to: 224,
                barColor: '#FFAD00',
            },
            {
                text: 'Der Text sollte idealerweise 250 Zeichen oder mehr umfassen.',
                from: LONG_MESSAGE_THRESHOLD,
                to: 99999999,
                barColor: '#02B504',
            },
        ],
    }),
    computed: {
        isTourInquiry() {
            return !!this.$store.getters['tour/route'];
        },
        isFollowUpInquiry() {
            return this.inquiryType === 'follow-up-inquiry' && !this.isTourInquiry;
        },
        isAsiInquiry() {
            return this.$store.getters['tour/isAsiInquiry'];
        },
        motivator() {
            let steps;
            if (this.$route.name.includes('updateMessage')) {
                steps = this.updateMessageMotivatorSteps;
            } else if (this.isFollowUpInquiry || this.isTourInquiry) {
                steps = this.followUpMotivatorSteps;
            } else if (this.isAsiInquiry) {
                steps = this.asiMotivatorSteps;
            } else {
                steps = this.motivatorSteps;
            }
            
            return steps.find((step) => {
                return this.message.length >= step.from && this.message.length <= step.to
            })
        },
        progressBar() {
            const progressPercentile = this.message ? this.message.length / 2.25 : 0
            return {
                "max-width": progressPercentile + "%",
                "background-color": this.motivator ? this.motivator.barColor : 'var(--brand-primary)',
            }
        },
        travelMessageInputTitle() {
            if(this.isFollowUpInquiry) {
                return 'Was möchtest Du in ' + this.countryName + ' erleben?'
            } else if (this.isTourInquiry) {
                return 'Entspricht diese Reiseidee schon Deinen Vorstellungen? Was gefällt Dir daran besonders gut? Was nicht?'
            }
            if (this.isAsiInquiry) {
                return 'Erzähle uns von deiner Traumreise?'
            }
            return 'Erzähle Deinem lokalen Reiseexperten von Deiner Traumreise.'
        },
        travelMessageInputPlaceholder() {
            if(this.isFollowUpInquiry) {
                return this.$t('messageBox.followUpPlaceholder')
            } else if (this.isTourInquiry) {
                return this.$t('messageBox.tourPlaceholder')
            }
            return this.$t('messageBox.placeholder', { country: this.countryName })
        },
    },
    watch: {
        value: {
            immediate: true,
            handler(newValue) {
                this.message = newValue;
            }
        },
        message(newValue) {
            this.$emit('input', newValue);
        }
    },
    mounted() {
        if(this.$store.getters['tour/travelData'].message) {
            this.message = this.$store.getters['tour/travelData'].message;
        }
    },
    methods: {
        storeMessage() {
            this.$store.commit('tour/setMessage', this.message)
        },
        handleBlur() {
            this.isActive = false;
            this.storeMessage();
        },
    },
}
</script>

<style scoped>
textarea:focus,
textarea:focus-visible {
    -webkit-tap-highlight-color: transparent;
    -webkit-appearance: none;
    outline: none;
}

/* Ensure the container maintains its border styles when focused */
.tw-border-2 {
    border-width: 2px;
}
</style>
